/**
 * AdminPagesListSection - Renders the page registry as a Page Builder section
 * 
 * This component enables recursive self-editing: the Page Builder index page
 * is itself managed by the Page Builder, with this section rendering the 
 * page registry list.
 * 
 * Used by PAGE_BUILDER_INDEX page type.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Layout, Globe, Home, Rocket, Loader2, Plus,
  ExternalLink, Clock, Search, Grid, List, Eye, Lock,
  BookOpen, Users, Image as ImageIcon, Award, Settings, Compass, FileText
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon map for page types
const PAGE_TYPE_ICONS = {
  HOMEPAGE: Home,
  LANDING: Rocket,
  DISCOVERY: Compass,
  BROWSE: Grid,
  COMIC_SERIES: BookOpen,
  NOVEL_SERIES: BookOpen,
  CHAPTER: FileText,
  READER_VERTICAL: BookOpen,
  READER_HORIZONTAL: BookOpen,
  READER_PAGE_BY_PAGE: BookOpen,
  CREATOR_PROFILE: Users,
  CREATOR_DASHBOARD: Users,
  GALLERY: ImageIcon,
  LEADERBOARD: Award,
  ABOUT: FileText,
  FAQ: FileText,
  PRIVACY: FileText,
  TERMS: FileText,
  PAGE_BUILDER_INDEX: Layout,
  ADMIN_DASHBOARD: Settings,
  SITE_MANAGER: Settings,
  CUSTOM: Globe,
};

const getPageIcon = (pageType) => PAGE_TYPE_ICONS[pageType] || Globe;

const AdminPagesListSection = ({ 
  settings = {},
  isPreview = false,
  onDataLoaded,
}) => {
  const navigate = useNavigate();
  
  // Props from section settings
  const {
    title = 'All Pages',
    showStats = true,
    showSearch = true,
    showFilters = true,
    defaultFilter = 'all',
    defaultView = 'grid',
  } = settings;
  
  // State
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterVisibility, setFilterVisibility] = useState(defaultFilter);
  const [viewMode, setViewMode] = useState(defaultView);

  // Fetch page registry
  useEffect(() => {
    const fetchPages = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/api/admin/page-registry`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPages(response.data.pages || []);
        onDataLoaded?.(response.data);
      } catch (error) {
        console.error('Error fetching page registry:', error);
        if (!isPreview) {
          toast.error('Failed to load pages');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchPages();
  }, [isPreview, onDataLoaded]);

  // Filtered pages
  const filteredPages = useMemo(() => {
    return pages.filter(page => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!page.title.toLowerCase().includes(query) &&
            !page.slug.toLowerCase().includes(query) &&
            !page.pageType.toLowerCase().includes(query)) {
          return false;
        }
      }
      if (filterVisibility !== 'all' && page.visibility !== filterVisibility) {
        return false;
      }
      return true;
    });
  }, [pages, searchQuery, filterVisibility]);

  const publicPages = filteredPages.filter(p => p.visibility === 'public');
  const adminPages = filteredPages.filter(p => p.visibility === 'admin-only');

  const getEditorUrl = (pageId, pageType) => {
    if (pageId === 'homepage') return '/admin/page-builder/homepage';
    if (pageId === 'landing_early_access') return '/admin/page-builder/landing';
    if (['COMIC_SERIES', 'NOVEL_SERIES', 'CHAPTER', 'CREATOR_PROFILE'].includes(pageType)) {
      return `/admin/page-builder/template/${pageId}`;
    }
    return `/admin/page-builder/${pageId}`;
  };

  const PageCard = ({ page }) => {
    const Icon = getPageIcon(page.pageType);
    const editorUrl = getEditorUrl(page.pageId, page.pageType);
    const hasDefinition = page.has_definition || page.has_draft || page.has_published;
    
    return (
      <Card 
        className={`bg-zinc-900 border-zinc-800 transition-all cursor-pointer group
          ${hasDefinition ? 'hover:border-orange-500/50' : 'hover:border-yellow-500/50 opacity-75'}`}
        onClick={() => !isPreview && navigate(editorUrl)}
        data-testid={`page-card-${page.pageId}`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className={`p-3 rounded-lg group-hover:scale-105 transition-transform
              ${page.visibility === 'admin-only' ? 'bg-purple-500/10' : 'bg-orange-500/10'}`}>
              <Icon className={`w-5 h-5 ${page.visibility === 'admin-only' ? 'text-purple-400' : 'text-orange-500'}`} />
            </div>
            <div className="flex gap-1.5 flex-wrap justify-end">
              {page.is_system && (
                <Badge variant="outline" className="border-zinc-600 text-zinc-400 text-[10px]">System</Badge>
              )}
              {page.visibility === 'admin-only' && (
                <Badge variant="outline" className="border-purple-500/50 text-purple-400 text-[10px]">
                  <Lock className="w-2.5 h-2.5 mr-1" />Admin
                </Badge>
              )}
              {page.has_published && (
                <Badge variant="outline" className="border-green-500/50 text-green-400 text-[10px]">Live</Badge>
              )}
              {page.has_draft && !page.has_published && (
                <Badge variant="outline" className="border-yellow-500/50 text-yellow-400 text-[10px]">Draft</Badge>
              )}
              {!hasDefinition && (
                <Badge variant="outline" className="border-zinc-600 text-zinc-500 text-[10px]">No Definition</Badge>
              )}
            </div>
          </div>
          <CardTitle className="text-white text-base mt-3">{page.title}</CardTitle>
          <CardDescription className="text-zinc-500 text-sm line-clamp-2">
            {page.description || `${page.pageType} page`}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-zinc-500">
              <Globe className="w-3.5 h-3.5" />
              <span className="truncate max-w-[120px]">{page.slug}</span>
            </div>
            {page.visibility === 'public' && (
              <a
                href={page.slug}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-orange-400 hover:text-orange-300"
              >
                <ExternalLink className="w-3.5 h-3.5" />View
              </a>
            )}
          </div>
          {page.updatedAt && (
            <div className="flex items-center gap-1.5 text-[10px] text-zinc-600 mt-3 pt-2 border-t border-zinc-800/50">
              <Clock className="w-3 h-3" />
              <span>Updated {new Date(page.updatedAt).toLocaleDateString()}</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="py-8 px-4 md:px-8" data-testid="admin-pages-list-section">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          <Button
            onClick={() => window.dispatchEvent(new CustomEvent('open-new-page-modal'))}
            size="sm"
            className="bg-orange-500 hover:bg-orange-600 text-white"
            disabled={isPreview}
            data-testid="new-page-button-inline"
          >
            <Plus className="w-4 h-4 mr-1" />New Page
          </Button>
        </div>

        {/* Stats */}
        {showStats && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <div className="text-2xl font-bold text-white">{pages.length}</div>
              <div className="text-xs text-zinc-500">Total Pages</div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-400">
                {pages.filter(p => p.has_published).length}
              </div>
              <div className="text-xs text-zinc-500">Published</div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-400">
                {pages.filter(p => p.has_draft && !p.has_published).length}
              </div>
              <div className="text-xs text-zinc-500">Drafts</div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <div className="text-2xl font-bold text-zinc-500">
                {pages.filter(p => !p.has_definition && !p.has_draft && !p.has_published).length}
              </div>
              <div className="text-xs text-zinc-500">No Definition</div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        {(showSearch || showFilters) && (
          <div className="flex items-center gap-3">
            {showSearch && (
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search pages..."
                  className="pl-9 bg-zinc-900 border-zinc-700 text-white"
                  disabled={isPreview}
                />
              </div>
            )}
            
            {showFilters && (
              <>
                <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-1 border border-zinc-700">
                  {['all', 'public', 'admin-only'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => !isPreview && setFilterVisibility(filter)}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        filterVisibility === filter 
                          ? 'bg-zinc-700 text-white' 
                          : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      {filter === 'admin-only' ? 'Admin' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </button>
                  ))}
                </div>
                
                <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-1 border border-zinc-700">
                  <button
                    onClick={() => !isPreview && setViewMode('grid')}
                    className={`p-1.5 rounded-md transition-colors ${
                      viewMode === 'grid' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => !isPreview && setViewMode('list')}
                    className={`p-1.5 rounded-md transition-colors ${
                      viewMode === 'list' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Page Lists */}
      <div className="max-w-7xl mx-auto">
        {/* Public Pages */}
        {publicPages.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-zinc-400 mb-4 flex items-center gap-2">
              <Eye className="w-4 h-4" />Public Pages ({publicPages.length})
            </h3>
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' 
              : 'space-y-2'
            }>
              {publicPages.map(page => <PageCard key={page.pageId} page={page} />)}
            </div>
          </div>
        )}

        {/* Admin Pages */}
        {adminPages.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-zinc-400 mb-4 flex items-center gap-2">
              <Lock className="w-4 h-4" />Admin Pages ({adminPages.length})
            </h3>
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' 
              : 'space-y-2'
            }>
              {adminPages.map(page => <PageCard key={page.pageId} page={page} />)}
            </div>
          </div>
        )}

        {/* Empty state */}
        {filteredPages.length === 0 && (
          <div className="text-center py-12">
            <div className="text-zinc-500 mb-2">No pages found</div>
            <div className="text-zinc-600 text-sm">
              {searchQuery ? 'Try a different search term' : 'Page registry is empty'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPagesListSection;
